import { useCallback, useMemo } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { Form, AutoComplete } from "antd";
import { useSelector } from "react-redux";

import { RVCollapse, RVButton, RVFormFloatingLabelWrap, RVHighlightedString } from "@regal-voice/ui";
import { generateFormItemRequiredRules } from "Components/FormItems/Shared/Validators/InputValidators/InputValidators";
import { caseInsensitiveFilter } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/Components/JourneyActionsPanel/NodeEditorPanel/shared";
import { useEventNamesQuery } from "ServerData/EventSchema/Hooks/useEventNamesQuery/useEventNamesQuery";
import { useEventPropertiesQuery } from "ServerData/EventSchema/Hooks/useEventPropertiesQuery/useEventPropertiesQuery";
import {
    // selectors
    selectFormFields,
    // actions
    setFields,
} from "Services/state/journeys/JourneyStateSlice";
import { useRVDispatch } from "Services/state/Storage";

import editorStyles from "../../../../../../NodeEditorPanel.module.scss";
import { UpdateContactForm } from "../../../../constants";
import { generateTriggerEventProperty, extractFormFieldName } from "../../../../utils";
import { useUpdateContactPanels } from "../../../Shared/Hooks/useUpdateContactPanels/useUpdateContactPanels";
import { TriggerEventPropertiesFormItems } from "./Components/TriggerEventPropertiesFormItems/TriggerEventPropertiesFormItems";

import type { EventSchemaOptionEntry, OptionsData } from "ServerData/EventSchema/eventSchemaTypes";

import styles from "./TriggerEventPanelLayout.module.scss";

type TriggerEventFormProps = {
    activePanels: number[];
};

type ContactAttrTriggerFormPanelContentProps = {
    loading: boolean;
    eventSchema: EventSchemaOptionEntry;
    eventName: string;
};

function createEventNameValue(v: any, h?: string) {
    return { value: v, label: <RVHighlightedString val={v} highlightVal={h} /> };
}

function createEventNameOptionValues(values?: Array<any>, highlight?: string): OptionsData {
    return !!values ? values.map((v) => createEventNameValue(v, highlight)) : [];
}

const EVENT_NAME_LABEL = "Event Name";
const REQUIRED_RULES = generateFormItemRequiredRules(EVENT_NAME_LABEL);

export function TriggerEventPanelsLayout({ activePanels }: TriggerEventFormProps): JSX.Element {
    const [form] = Form.useForm();
    const dispatch = useRVDispatch();

    const fields = useSelector(selectFormFields);

    const eventName = useMemo(() => {
        const eventNameField = fields.find((field: any) => {
            return extractFormFieldName(field) === "eventName";
        });
        if (eventNameField) {
            return eventNameField.value;
        }
        return "";
    }, [fields]);

    const { loading: loadingEvts, eventNames } = useEventNamesQuery("custom");
    const { loading: loadingProps, eventSchema } = useEventPropertiesQuery("custom", eventName);

    const loading = loadingEvts || loadingProps;

    const hasPanels = fields.some((field: any) => {
        return extractFormFieldName(field).includes(UpdateContactForm.propertiesListName);
    });

    const { addFormPanel, panelsList } = useUpdateContactPanels<ContactAttrTriggerFormPanelContentProps>({
        form,
        generatePanel: generateTriggerEventProperty,
        listName: UpdateContactForm.propertiesListName,
        itemName: UpdateContactForm.propertyName,
        // @ts-expect-error complex types
        itemComponent: TriggerEventPropertiesFormItems,
        itemComponentProps: {
            loading,
            eventSchema,
            eventName,
        },
    });
    const handleEventSelect = useCallback(
        (val: string) => {
            const filteredFields = fields.filter((field: any) => {
                return extractFormFieldName(field) !== "eventName";
            });
            const furtherFiltered = filteredFields.filter((field: any) => {
                return !extractFormFieldName(field).includes(UpdateContactForm.propertiesListName);
            });
            const eventNameField = { name: "eventName", value: val };
            const fullFields = [...furtherFiltered, eventNameField];
            // We need to dispatch the changes to the store, beause we rerender the form
            // based on the fields in the store
            dispatch(setFields(fullFields));
        },
        [dispatch, fields]
    );

    return (
        <div>
            <RVFormFloatingLabelWrap
                name={UpdateContactForm.eventName}
                rules={REQUIRED_RULES}
                label={EVENT_NAME_LABEL}
                noPadding
            >
                <AutoComplete
                    options={createEventNameOptionValues(eventNames)}
                    onChange={handleEventSelect}
                    filterOption={caseInsensitiveFilter}
                />
            </RVFormFloatingLabelWrap>
            {hasPanels && <div className={editorStyles.divider} />}
            <>
                <RVCollapse isChild panelsList={panelsList} activeKey={activePanels}></RVCollapse>
                <RVButton
                    className={styles.buttonAdjust}
                    type="link"
                    onClick={addFormPanel}
                    icon={<PlusCircleOutlined />}
                >
                    Add Property
                </RVButton>
            </>
        </div>
    );
}
